import styled from 'styled-components';

/**
 * Toolbar primitives shared by the admin list pages: one baseline for search,
 * selects, date fields and buttons, so every page's filter row lines up.
 * Extracted from the Logs page, which still re-exports them.
 */

export const ToolbarRow = styled.div<{ $flush?: boolean }>`
  display: flex;
  align-items: flex-end;
  gap: 0.75rem 1rem;
  margin-bottom: ${({ $flush }) => ($flush ? '0.75rem' : '1.25rem')};
  flex-wrap: nowrap;

  &:last-of-type {
    margin-bottom: ${({ $flush }) => ($flush ? 0 : '1.25rem')};
  }
`;

/** Left cluster of filters — grows so actions can sit on the right. */
export const ToolbarFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 0.75rem;
  flex: 1 1 auto;
  min-width: 0;
`;

/** Keeps From + To on one line when the toolbar wraps. */
export const DateRangeGroup = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-end;
  gap: 0.75rem;
  flex: 0 0 auto;
`;

/** Shared labeled control shell so search / select / date / buttons share one baseline. */
export const FieldControl = styled.div<{ $width?: string }>`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  width: ${({ $width }) => $width || '9.5rem'};
  max-width: 100%;
  flex: 0 1 ${({ $width }) => $width || '9.5rem'};
  min-width: 0;

  & > * {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }
`;

export const SearchControl = styled(FieldControl).attrs({ $width: '14rem' })``;

export const FilterControl = styled(FieldControl).attrs({ $width: '9.5rem' })``;

export const DateControl = styled(FieldControl).attrs({ $width: '10rem' })``;

export const FieldLabel = styled.label`
  font-size: 0.7rem;
  font-weight: 600;
  color: ${({ theme }) => theme.midGrey};
  text-transform: uppercase;
  letter-spacing: 0.02em;
  line-height: 1;
`;

export const DateLabel = FieldLabel;

export const DateInput = styled.input`
  height: 2.81rem;
  box-sizing: border-box;
  padding: 0 0.85rem;
  border: 1px solid ${({ theme }) => theme.grey};
  border-radius: 0.5rem;
  background-color: ${({ theme }) => theme.background};
  color: ${({ theme }) => theme.dark};
  font-family: inherit;
  font-size: 0.875rem;
  color-scheme: ${({ theme }) => (theme.dark === '#FFFFFF' ? 'dark' : 'light')};
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    background-color 0.2s ease;

  &::-webkit-calendar-picker-indicator {
    cursor: pointer;
    opacity: 0.7;
  }

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.midGrey};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.lightGrey};
  }

  &:disabled {
    border-color: ${({ theme }) => theme.grey};
    color: ${({ theme }) => theme.midGrey};
    background-color: ${({ theme }) => theme.lightGrey};
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const ToggleButton = styled.button<{ $active: boolean }>`
  height: 2.81rem;
  padding: 0 0.9rem;
  border-radius: 0.5rem;
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.primary : theme.grey)};
  background: ${({ theme, $active }) =>
    $active ? theme.primary : theme.background};
  color: ${({ theme, $active }) =>
    $active ? theme.alwaysLight : theme.dark};
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    background-color 0.2s ease,
    color 0.2s ease;

  &:hover:not(:disabled) {
    border-color: ${({ theme, $active }) => ($active ? theme.primary : theme.midGrey)};
    box-shadow: ${({ $active }) => ($active ? '0 6px 20px rgba(0, 0, 0, 0.12)' : 'none')};
    filter: ${({ $active }) => ($active ? 'brightness(1.05)' : 'none')};
  }

  &:focus-visible {
    outline: none;
    border-color: ${({ theme }) => theme.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.lightGrey};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const ToolbarActions = styled.div`
  display: inline-flex;
  flex-direction: column;
  gap: 0.35rem;
  flex: 0 0 auto;
  margin-left: auto;

  /* Match FieldLabel height so buttons share the input baseline */
  &::before {
    content: '\\00a0';
    font-size: 0.7rem;
    font-weight: 600;
    line-height: 1;
    letter-spacing: 0.02em;
    visibility: hidden;
  }
`;

export const ToolbarActionButtons = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
`;

export const ToolButton = styled.button<{ $iconOnly?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  height: 2.81rem;
  min-width: 2.81rem;
  padding: ${({ $iconOnly }) => ($iconOnly ? '0' : '0 0.9rem')};
  border-radius: 0.5rem;
  border: 1px solid ${({ theme }) => theme.grey};
  background: ${({ theme }) => theme.background};
  color: ${({ theme }) => theme.dark};
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition:
    border-color 0.2s ease,
    background-color 0.2s ease,
    color 0.2s ease;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.midGrey};
    background: ${({ theme }) => theme.lightGrey};
  }

  &:focus-visible {
    outline: none;
    border-color: ${({ theme }) => theme.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.lightGrey};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    flex-shrink: 0;
  }
`;
